<template>
  <div>
    <Message type="info">
      キーワード判定セットを作成すると、キャンペーン作成時のポジティブワード・ネガティブワードの設定が簡単にできます。
      <br />
      すでに作成済みのキャンペーンには、キーワードセットの編集やデフォルト設定の切り替えは反映されません。
    </Message>

    <el-button class="setting-table-add-action" type="primary" @click="add">
      <Icon name="plus" />
      キーワード判定セットの追加
    </el-button>

    <el-table v-if="is_exist" class="setting-table" :data="api_profile_keyword_settings" @sort-change="sortChange">
      <el-table-column prop="name" label="キーワード判定セット" width="200" sortable />
      <el-table-column prop="keyword" label="キーワード" min-width="300">
        <template slot-scope="prop">
          <div class="setting-table-keyword">
            <div v-for="(keyword, index) of prop.row.positive_keyword" :key="index" class="setting-table-keyword-item">
              <Keyword type="positive" :text="keyword" />
            </div>
          </div>

          <div class="setting-table-keyword">
            <div v-for="(keyword, index) of prop.row.negative_keyword" :key="index" class="setting-table-keyword-item">
              <Keyword type="warning" :text="keyword" />
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="is_active" label="デフォルト設定" width="150">
        <template slot-scope="prop">
          <div class="setting-table-default-config">
            <el-switch
              class="setting-table-switch"
              :value="prop.row.is_active"
              :active-text="prop.row.is_active ? 'オン' : 'オフ'"
              @change="changeDefaultConfig(prop.row)"
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="action" width="100">
        <template slot-scope="prop">
          <div class="setting-table-action">
            <div class="setting-table-action-item">
              <ButtonIcon name="table-edit" tooltip="編集" @click="edit(prop.row.id)" />
            </div>
            <div class="setting-table-action-item">
              <ButtonIcon name="content-duplicate" tooltip="複製" @click="copy(prop.row.id)" />
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Account from '@/client/components/_molecules/Account.vue'
import ButtonIcon from '@/client/components/_molecules/ButtonIcon.vue'
import Keyword from '@/client/components/_molecules/Keyword.vue'
import { TrackingService } from '@/client/services'
import { useProfileKeywordSettingStore } from '@/client/store/useProfileKeywordSettingStore'
import { ProfileKeywordSetting } from '@/typings/profile_keyword_settings'

export default defineComponent({
  components: {
    Icon,
    Message,
    Account,
    ButtonIcon,
    Keyword
  },
  computed: {
    ...mapState(useProfileKeywordSettingStore, ['api_profile_keyword_settings']),
    is_exist() {
      return this.api_profile_keyword_settings.length > 0
    }
  },
  methods: {
    ...mapActions(useProfileKeywordSettingStore, ['updateProfileKeyword']),
    add(payload: unknown) {
      TrackingService.sendEvent('click:グループ設定|自己紹介キーワード設定|キーワード判定セットの追加')
      this.$emit('add', payload)
      return payload
    },
    edit(payload: unknown) {
      TrackingService.sendEvent('click:グループ設定|自己紹介キーワード設定|編集')
      this.$emit('edit', payload)
      return payload
    },
    copy(payload: unknown) {
      TrackingService.sendEvent('click:グループ設定|自己紹介キーワード設定|複製')
      this.$emit('copy', payload)
      return payload
    },

    /**
     * 自己紹介キーワード判定のデフォルト設定を変更
     */
    async changeDefaultConfig(data: ProfileKeywordSetting) {
      TrackingService.sendEvent('switch:グループ設定|自己紹介キーワード設定|デフォルト判定')

      const value = {
        keyword_setting_id: data.id,
        is_active: !data.is_active
      }

      const response = await this.updateProfileKeyword(value)

      if (response.error && response.error.type === 'MAX_COUNT_OVER') {
        this.$notify({
          title: 'デフォルト設定の変更に失敗しました。',
          message: 'ポジティブキーワードまたはネガティブキーワードの合計が200文字以内になるように設定してください。',
          customClass: 'danger'
        })

        return
      }

      if (response.data) {
        this.$notify({
          title: '設定を変更しました。',
          message: '',
          customClass: 'success'
        })

        return
      }

      this.$notify({
        title: 'デフォルト設定の変更に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger'
      })
    },

    sortChange() {
      TrackingService.sendEvent('sort:グループ設定|自己紹介キーワード設定|キーワード判定セット')
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.setting-table,
.setting-table-add-action {
  margin-top: $spacing-unit * 2;
}

.setting-table-keyword {
  display: flex;
  flex-flow: wrap;

  .setting-table-keyword-item {
    margin: math.div($spacing-unit, 2);
  }
}

.setting-table-default-config {
  display: flex;
  align-items: center;

  .setting-table-switch {
    margin-right: $spacing-unit;
  }
}

.setting-table-action {
  display: flex;

  .setting-table-action-item:not(:last-child) {
    margin-right: math.div($spacing-unit, 2);
  }
}
</style>
